import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { List, X, CaretDown } from "@phosphor-icons/react";

const Logo = () => (
  <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo-link">
    <div className="relative w-9 h-9 bg-[#0a0a0a] text-white flex items-center justify-center font-display font-semibold text-sm tracking-tight transition-colors group-hover:bg-[#0A4DFF]">
      <span>GM</span>
      <span className="absolute right-0 top-0 w-2 h-2 bg-[#0A4DFF] group-hover:bg-white" />
    </div>
    <div className="leading-none whitespace-nowrap">
      <div className="font-display font-semibold text-[15px] tracking-tight">Global Marketing</div>
      <div className="gm-eyebrow text-[10px] mt-0.5">Future of retail</div>
    </div>
  </Link>
);

const LangSwitch = () => {
  const { lang, setLang, t } = useLang();
  return (
    <div className="flex border border-[#0a0a0a]/15" role="group" aria-label="language switch" data-testid="language-switcher">
      <button
        onClick={() => setLang("cs")}
        data-testid="lang-cs-button"
        className={`px-3 py-1.5 text-xs font-mono tracking-wider transition-colors ${
          lang === "cs" ? "bg-[#0a0a0a] text-white" : "bg-white text-[#0a0a0a] hover:bg-[#f0f4ff]"
        }`}
      >
        {t.common.czech}
      </button>
      <button
        onClick={() => setLang("sk")}
        data-testid="lang-sk-button"
        className={`px-3 py-1.5 text-xs font-mono tracking-wider transition-colors border-l border-[#0a0a0a]/15 ${
          lang === "sk" ? "bg-[#0a0a0a] text-white" : "bg-white text-[#0a0a0a] hover:bg-[#f0f4ff]"
        }`}
      >
        {t.common.slovak}
      </button>
      <button
        onClick={() => setLang("en")}
        data-testid="lang-en-button"
        className={`px-3 py-1.5 text-xs font-mono tracking-wider transition-colors border-l border-[#0a0a0a]/15 ${
          lang === "en" ? "bg-[#0a0a0a] text-white" : "bg-white text-[#0a0a0a] hover:bg-[#f0f4ff]"
        }`}
      >
        {t.common.english}
      </button>
    </div>
  );
};

const pillarLinks = (t) => [
  { to: "/merchandising-services", title: t.pillars.merchandising.title, kicker: t.pillars.merchandising.kicker, desc: t.pillars.merchandising.desc, testid: "menu-merchandising" },
  { to: "/instore-data", title: t.pillars.instore.title, kicker: t.pillars.instore.kicker, desc: t.pillars.instore.desc, testid: "menu-instore" },
  { to: "/shelfmasters", title: t.pillars.shelfmasters.title, kicker: t.pillars.shelfmasters.kicker, desc: t.pillars.shelfmasters.desc, testid: "menu-shelfmasters" },
  { to: "/retail-digitalisation", title: t.pillars.digital.title, kicker: t.pillars.digital.kicker, desc: t.pillars.digital.desc, testid: "menu-digital" },
  { to: "/retail-consulting", title: t.pillars.consulting.title, kicker: t.pillars.consulting.kicker, desc: t.pillars.consulting.desc, testid: "menu-consulting" },
  { to: "/pos", title: t.pillars.pos.title, kicker: t.pillars.pos.kicker, desc: t.pillars.pos.desc, testid: "menu-pos" },
];

const SolutionsDropdown = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const items = pillarLinks(t);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="solutions-dropdown"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-sm tracking-tight text-[#0a0a0a] hover:text-[#0A4DFF] transition-colors"
        aria-expanded={open}
        data-testid="solutions-trigger"
      >
        <span>{t.nav.pillars}</span>
        <CaretDown size={12} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[760px] max-w-[calc(100vw-2rem)]">
          <div className="bg-white border border-[#e5e5e5] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.18)] grid grid-cols-1 sm:grid-cols-2 divide-x divide-y sm:divide-y-0 divide-[#e5e5e5]">
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                data-testid={it.testid}
                className="group p-5 hover:bg-[#fafafa] transition-colors"
              >
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-[10px] tracking-widest text-[#9a9a9a]">{it.kicker}</span>
                  <h4 className="font-display text-base md:text-lg tracking-tight group-hover:text-[#0A4DFF] transition-colors">
                    {it.title}
                  </h4>
                </div>
                <p className="text-xs text-[#525252] mt-2 leading-relaxed line-clamp-2">{it.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-2xl bg-white/75 border-b border-[#e5e5e5]"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          <Link
            to="/about"
            data-testid="nav-about"
            className="text-sm tracking-tight text-[#0a0a0a] hover:text-[#0A4DFF] transition-colors gm-link whitespace-nowrap"
          >
            {t.nav.about}
          </Link>
          <SolutionsDropdown />
          <Link
            to="/kontakt"
            data-testid="nav-contact"
            className="text-sm tracking-tight text-[#0a0a0a] hover:text-[#0A4DFF] transition-colors gm-link whitespace-nowrap"
          >
            {t.nav.contact}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitch />
          <button
            className="lg:hidden p-2 border border-[#e5e5e5]"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <List size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#e5e5e5] bg-white" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            <Link to="/about" className="py-2 text-base text-[#0a0a0a] hover:text-[#0A4DFF]" data-testid="mobile-nav-about">{t.nav.about}</Link>
            <span className="gm-eyebrow mt-4 mb-2">{t.nav.pillars}</span>
            {pillarLinks(t).map((it) => (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`mobile-${it.testid}`}
                className="py-2 text-base text-[#0a0a0a] hover:text-[#0A4DFF] flex items-baseline gap-3"
              >
                <span className="font-mono text-[10px] text-[#9a9a9a]">{it.kicker}</span>
                {it.title}
              </Link>
            ))}
            <Link to="/kontakt" className="py-2 mt-4 text-base text-[#0a0a0a] hover:text-[#0A4DFF]" data-testid="mobile-nav-contact">{t.nav.contact}</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
